/**
 * aha-time-range-input - Advanced time range selection component
 *
 * This component provides a sophisticated time range input with extensive edge case handling
 * for a variety of time formats. It's designed to be extremely forgiving of user input while
 * maintaining accurate time data for the server.
 *
 * There is a ton of edge case handling in here for a variety of inputs to make the ultimate
 * time picker experience, but the basics are to:
 * 1. handle keypresses from user
 * 2. fudge values around based on all that nuance
 * 3. maintain a hidden input that sends valid time ranges to the server
 *
 * Utility files:
 * - time-utils.ts: Handles parsing, formatting, and calculations for time values
 *   with support for various input formats (12h/24h, with/without colons, etc.)
 * - time-dropdown.ts: Manages the dropdown UI for time selection, including
 *   generating time options and handling selection
 *
 * The component maintains both visible inputs for user interaction and hidden inputs
 * that contain properly formatted time values for form submission.
 */
import { Component, Host, h, Prop, State, Event, EventEmitter } from '@stencil/core';
import {
  parseTimeInput,
  formatTime,
  convertTimeToMinutes,
  calculateEndTime,
  calculateDuration,
  ParsedTime,
  convertTo24HourFormat,
} from './time-utils';
import {
  TimeOption,
  generateStandardTimeOptions,
  generateEndTimeOptions,
  populateDropdown,
} from './time-dropdown';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface AhaTimeRangeInput extends HTMLAhaTimeRangeInputElement {}

/**
 * A component for selecting a time range. The `<aha-time-range-input>` component is a wrapper around two `<input type="text">` elements.
 * It provides a parsing function that is forgiving of user input, and will automatically adjust the end time if it is before the start time.
 */

@Component({
  tag: 'aha-time-range-input',
  styleUrl: 'aha-time-range-input.css',
})
export class AhaTimeRangeInput {
  /** Optional. The name of the backing start time input that will be sent to the server as part of a regular `<form>` submit. */
  @Prop({ reflect: true }) startTimeName?: string;

  /** Optional. The value of the input. */
  @Prop({ reflect: true }) startTimeValue?: string;

  /** Optional. default time in a 09:00am */
  @Prop({ reflect: true }) defaultStartTime?: string;

  /** Optional. The name of the backing end time input that will be sent to the server as part of a regular `<form>` submit. */
  @Prop({ reflect: true }) endTimeName?: string;

  /** Optional. The value of the end time input. */
  @Prop({ reflect: true }) endTimeValue?: string;

  /** Optional. default time in a 09:00am */
  @Prop({ reflect: true }) defaultEndTime?: string;

  /** Optional. The class to apply to the input. */
  @Prop({ reflect: true }) customInputClass?: string;

  /** Optional. If true, the input will be disabled. */
  @Prop({ reflect: true }) readonly?: boolean;

  /** Optional. The timezone to use when parsing the time. */
  @Prop({ reflect: true }) timeZone?: string;

  /** Optional. If true, the end time will automatically adjust to be after the start time. */
  @Prop({ reflect: true }) autoAdjustEndTime?: boolean = true;

  /**
   * Triggers when the start time changes.
   */
  @Event({
    eventName: 'aha-time-range-input:start-time-change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  startTimeChangeEvent!: EventEmitter<string>;

  /**
   * Triggers when the end time changes.
   */
  @Event({
    eventName: 'aha-time-range-input:end-time-change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  endTimeChangeEvent!: EventEmitter<string>;

  /**
   * Triggers when the duration between start and end time changes.
   */
  @Event({
    eventName: 'aha-time-range-input:duration-change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  durationChangeEvent!: EventEmitter<number>;

  // Internal state
  @State() private single: boolean = false;
  @State() private currentDurationMinutes: number = 0;
  @State() private hasEndTimeBeenSet: boolean = false;
  @State() private startTimeOptions: TimeOption[] = [];
  @State() private endTimeOptions: TimeOption[] = [];

  // DOM references
  private startTimeInput!: HTMLInputElement;
  private endTimeInput!: HTMLInputElement;
  private startTimeHiddenInput!: HTMLInputElement;
  private endTimeHiddenInput!: HTMLInputElement;
  private startTimeDropdown!: HTMLDivElement;
  private endTimeDropdown!: HTMLDivElement;

  connectedCallback() {
    // Determine if this is a single time input or a range
    this.single = !this.endTimeName;

    // Generate standard time options
    this.startTimeOptions = generateStandardTimeOptions();
  }

  componentDidLoad() {
    this.handleDefaults();

    // Initialize hidden inputs with 24-hour format values
    if (this.startTimeInput && this.startTimeInput.value) {
      this.updateStartTimeHiddenInput();
    }

    if (!this.single && this.endTimeInput && this.endTimeInput.value) {
      this.updateEndTimeHiddenInput();
    }
  }

  /**
   * Parse ISO8601 date string and extract time
   */
  private parseIso8601Time(isoString: string): ParsedTime | null {
    try {
      const timezone = this.timeZone ?? (window as any).currentUser?.zone ?? dayjs.tz.guess();
      const date = dayjs(isoString).tz(timezone);

      if (!date.isValid()) {
        return null;
      }

      return {
        hour: date.hour(),
        minute: date.minute(),
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Process a time value through various parsing methods
   */
  private processTimeValue(value?: string, defaultValue?: string): string | undefined {
    // First try the provided value
    if (value) {
      // Check if the value is in ISO8601 format
      const isoTime = this.parseIso8601Time(value);
      if (isoTime) {
        return formatTime(isoTime);
      } else {
        // Try regular time parsing
        const parsedTime = parseTimeInput(value);
        if (parsedTime.hour !== undefined) {
          return formatTime(parsedTime);
        } else {
          // Use as-is if parsing fails
          return value;
        }
      }
    }
    // If no value, try the default
    else if (defaultValue) {
      // Check if the default is in ISO8601 format
      const isoTime = this.parseIso8601Time(defaultValue);
      if (isoTime) {
        return formatTime(isoTime);
      } else {
        const parsedDefault = parseTimeInput(defaultValue);
        if (parsedDefault.hour !== undefined) {
          return formatTime(parsedDefault);
        }
      }
    }

    return undefined;
  }

  /**
   * Handle setting default values
   */
  private handleDefaults() {
    // Process start time
    const formattedStartTime = this.processTimeValue(this.startTimeValue, this.defaultStartTime);
    if (formattedStartTime) {
      this.startTimeInput.value = formattedStartTime;
      this.updateStartTimeHiddenInput();
    }

    // Process end time if not in single mode
    if (!this.single) {
      const formattedEndTime = this.processTimeValue(this.endTimeValue, this.defaultEndTime);
      if (formattedEndTime) {
        this.endTimeInput.value = formattedEndTime;
        this.updateEndTimeHiddenInput();
        this.hasEndTimeBeenSet = true;
        this.updateDurationFromInputs();
      }
    }
  }

  /**
   * Update the current duration based on start and end time inputs
   */
  private updateDurationFromInputs() {
    const startTimeMinutes = convertTimeToMinutes(this.startTimeInput.value);
    const endTimeMinutes = convertTimeToMinutes(this.endTimeInput.value, this.startTimeInput.value);
    const newDuration = calculateDuration(startTimeMinutes, endTimeMinutes);

    // Only emit event if duration has changed
    if (this.currentDurationMinutes !== newDuration) {
      this.currentDurationMinutes = newDuration;
      this.durationChangeEvent.emit(this.currentDurationMinutes);
    }
  }

  /**
   * Update end time based on the new start time and current duration
   */
  private updateEndTimeBasedOnDuration(newStartTimeMinutes: number) {
    if (!this.autoAdjustEndTime) {
      return;
    }

    const newEndTime = calculateEndTime(newStartTimeMinutes, this.currentDurationMinutes);
    const oldEndTime = this.endTimeInput.value;
    this.endTimeInput.value = formatTime(newEndTime);
    this.updateEndTimeHiddenInput();

    // Emit end time change event if the value has changed
    if (oldEndTime !== this.endTimeInput.value) {
      this.endTimeChangeEvent.emit(this.endTimeInput.value);
    }
  }

  /**
   * Show dropdown for time selection
   */
  private showDropdown(dropdown: HTMLDivElement, input: HTMLInputElement, isEndTime = false) {
    if (isEndTime) {
      // Generate end time options based on current start time
      const startTimeMinutes = convertTimeToMinutes(this.startTimeInput.value);
      this.endTimeOptions = generateEndTimeOptions(startTimeMinutes);

      // Find selected option based on current duration
      const selectedOption = this.endTimeOptions.find(
        opt => opt.durationMinutes === this.currentDurationMinutes,
      );

      // Populate dropdown
      populateDropdown(
        dropdown,
        this.endTimeOptions,
        this.handleEndTimeOptionClick,
        selectedOption,
      );
    } else {
      // Find selected option based on input value
      const parsedTime = parseTimeInput(input.value);
      const selectedOption = this.startTimeOptions.find(
        opt => opt.hour === parsedTime.hour && opt.minute === (parsedTime.minute || 0),
      );

      // Populate dropdown
      populateDropdown(
        dropdown,
        this.startTimeOptions,
        this.handleStartTimeOptionClick,
        selectedOption,
      );
    }
    // Position the dropdown relative to the input
    const rect = input.getBoundingClientRect();
    dropdown.style.top = `${rect.bottom + 5}px`;
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.display = 'block';
  }

  /**
   * Hide dropdown
   */
  private hideDropdown(dropdown: HTMLDivElement) {
    dropdown.style.display = 'none';
  }

  /**
   * Handle start time option click
   */
  private handleStartTimeOptionClick = (option: TimeOption) => {
    if (this.readonly) return;

    const oldStartTime = this.startTimeInput.value;

    // Set the new start time
    this.startTimeInput.value = option.timeString;
    this.updateStartTimeHiddenInput();
    this.hideDropdown(this.startTimeDropdown);

    // Emit start time change event if the value has changed
    if (oldStartTime !== option.timeString) {
      this.startTimeChangeEvent.emit(option.timeString);
    }

    // Only update end time if it's not empty and has been explicitly set
    if (this.hasEndTimeBeenSet && this.endTimeInput.value.trim() !== '') {
      const newStartTime = convertTimeToMinutes(option.timeString);
      this.updateEndTimeBasedOnDuration(newStartTime);
    }
  };

  /**
   * Handle end time option click
   */
  private handleEndTimeOptionClick = (option: TimeOption) => {
    if (this.readonly) return;

    const oldEndTime = this.endTimeInput.value;
    this.endTimeInput.value = option.timeString;
    this.updateEndTimeHiddenInput();
    this.hideDropdown(this.endTimeDropdown);

    // Emit end time change event if the value has changed
    if (oldEndTime !== option.timeString) {
      this.endTimeChangeEvent.emit(option.timeString);
    }

    // Update the duration when end time is selected
    this.hasEndTimeBeenSet = true;
    if (option.durationMinutes !== undefined) {
      const oldDuration = this.currentDurationMinutes;
      this.currentDurationMinutes = option.durationMinutes;

      // Emit duration change event if the value has changed
      if (oldDuration !== option.durationMinutes) {
        this.durationChangeEvent.emit(option.durationMinutes);
      }
    }
  };

  /**
   * Handle keyboard navigation in dropdowns
   */
  private handleKeydown(
    e: KeyboardEvent,
    input: HTMLInputElement,
    dropdown: HTMLDivElement,
    isEndTime = false,
  ) {
    if (this.readonly) return;

    if (e.key === 'Enter') {
      e.preventDefault();

      const selected = dropdown.querySelector('.time-option.selected') as HTMLElement;
      if (selected && selected.textContent) {
        // Extract time part without the duration for end time
        if (isEndTime) {
          const oldEndTime = this.endTimeInput.value;
          const timeOnly = selected.textContent.split(' ')[0];
          input.value = timeOnly;
          this.updateEndTimeHiddenInput();

          // Emit end time change event if the value has changed
          if (oldEndTime !== timeOnly) {
            this.endTimeChangeEvent.emit(timeOnly);
          }

          // Update the duration when end time is selected
          this.hasEndTimeBeenSet = true;
          const oldDuration = this.currentDurationMinutes;
          this.currentDurationMinutes = parseInt(selected.dataset.durationMinutes || '0');

          // Emit duration change event if the value has changed
          if (oldDuration !== this.currentDurationMinutes) {
            this.durationChangeEvent.emit(this.currentDurationMinutes);
          }
        } else {
          const oldStartTime = this.startTimeInput.value;
          input.value = selected.textContent;
          this.updateStartTimeHiddenInput();

          // Emit start time change event if the value has changed
          if (oldStartTime !== selected.textContent) {
            this.startTimeChangeEvent.emit(selected.textContent);
          }

          // If end time has been set and is not empty, maintain the duration
          if (this.hasEndTimeBeenSet && this.endTimeInput.value.trim() !== '') {
            const newStartTime = convertTimeToMinutes(input.value);
            this.updateEndTimeBasedOnDuration(newStartTime);
          }
        }

        this.hideDropdown(dropdown);
      } else {
        // Try to format what the user entered
        // For end time, pass the start time value for relative time formats
        const parsed = isEndTime
          ? parseTimeInput(input.value, this.startTimeInput.value)
          : parseTimeInput(input.value);
        const formatted = formatTime(parsed);

        if (formatted) {
          if (isEndTime) {
            const oldEndTime = this.endTimeInput.value;
            input.value = formatted;
            this.updateEndTimeHiddenInput();

            // Emit end time change event if the value has changed
            if (oldEndTime !== formatted) {
              this.endTimeChangeEvent.emit(formatted);
            }

            // Update the duration when end time is set manually
            this.hasEndTimeBeenSet = true;
            this.updateDurationFromInputs();
          } else {
            const oldStartTime = this.startTimeInput.value;
            input.value = formatted;
            this.updateStartTimeHiddenInput();

            // Emit start time change event if the value has changed
            if (oldStartTime !== formatted) {
              this.startTimeChangeEvent.emit(formatted);
            }

            if (this.hasEndTimeBeenSet && this.endTimeInput.value.trim() !== '') {
              // If this is the start time and end time is set and not empty, maintain duration
              const newStartTime = convertTimeToMinutes(input.value);
              this.updateEndTimeBasedOnDuration(newStartTime);
            }
          }
        }

        this.hideDropdown(dropdown);
      }
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const visible = Array.from(dropdown.querySelectorAll('.time-option'));
      const selected = dropdown.querySelector('.time-option.selected');
      const selectedIndex = selected ? visible.indexOf(selected) : 0;

      if (e.key === 'ArrowDown' && selectedIndex < visible.length - 1) {
        visible.forEach(opt => opt.classList.remove('selected'));
        visible[selectedIndex + 1].classList.add('selected');
        visible[selectedIndex + 1].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'ArrowUp' && selectedIndex > 0) {
        visible.forEach(opt => opt.classList.remove('selected'));
        visible[selectedIndex - 1].classList.add('selected');
        visible[selectedIndex - 1].scrollIntoView({ block: 'nearest' });
      }
    } else if (e.key === 'Escape') {
      this.hideDropdown(dropdown);
    }
  }

  // Event handlers for start time input
  private handleStartTimeInput = () => {
    if (this.readonly) return;
    this.showDropdown(this.startTimeDropdown, this.startTimeInput);
  };

  private handleStartTimeFocus = () => {
    if (this.readonly) return;
    this.showDropdown(this.startTimeDropdown, this.startTimeInput);
  };

  private handleStartTimeBlur = () => {
    if (this.readonly) return;

    const parsed = parseTimeInput(this.startTimeInput.value);

    if (Object.keys(parsed).length > 0 && parsed.hour !== undefined) {
      const oldStartTime = this.startTimeInput.value;
      this.startTimeInput.value = formatTime(parsed);
      this.updateStartTimeHiddenInput();

      // Emit start time change event if the value has changed
      if (oldStartTime !== this.startTimeInput.value) {
        this.startTimeChangeEvent.emit(this.startTimeInput.value);
      }

      // Only update end time if it has content
      if (this.hasEndTimeBeenSet && this.endTimeInput.value.trim() !== '') {
        const newStartTime = convertTimeToMinutes(this.startTimeInput.value);
        this.updateEndTimeBasedOnDuration(newStartTime);
      }
    }

    setTimeout(() => this.hideDropdown(this.startTimeDropdown), 200);
  };

  private handleStartTimeKeyDown = (e: KeyboardEvent) => {
    if (this.readonly) return;

    // If Tab is pressed, focus the end time input and prevent default behavior
    if (e.key === 'Tab' && !e.shiftKey && !this.single) {
      e.preventDefault();
      this.endTimeInput.focus();
      return;
    }

    this.handleKeydown(e, this.startTimeInput, this.startTimeDropdown);
  };

  // Event handlers for end time input
  private handleEndTimeInput = (e: Event) => {
    if (this.readonly) return;

    if ((e.target as HTMLInputElement).value.trim() === '') {
      this.hasEndTimeBeenSet = false;
    }
    this.showDropdown(this.endTimeDropdown, this.endTimeInput, true);
  };

  private handleEndTimeFocus = () => {
    if (this.readonly) return;
    this.showDropdown(this.endTimeDropdown, this.endTimeInput, true);
  };

  private handleEndTimeBlur = () => {
    if (this.readonly) return;

    // Pass the start time value to parseTimeInput for relative time formats
    const parsed = parseTimeInput(this.endTimeInput.value, this.startTimeInput.value);

    // If the input was cleared, reset the end time state
    if (this.endTimeInput.value.trim() === '') {
      this.hasEndTimeBeenSet = false;
    } else if (Object.keys(parsed).length > 0 && parsed.hour !== undefined) {
      const oldEndTime = this.endTimeInput.value;
      this.endTimeInput.value = formatTime(parsed);
      this.updateEndTimeHiddenInput();

      // Emit end time change event if the value has changed
      if (oldEndTime !== this.endTimeInput.value) {
        this.endTimeChangeEvent.emit(this.endTimeInput.value);
      }

      // Update the duration when end time is set manually
      this.hasEndTimeBeenSet = true;
      this.updateDurationFromInputs();
    }

    setTimeout(() => this.hideDropdown(this.endTimeDropdown), 200);
  };

  private handleEndTimeKeyDown = (e: KeyboardEvent) => {
    if (this.readonly) return;
    this.handleKeydown(e, this.endTimeInput, this.endTimeDropdown, true);
  };

  /**
   * Update the hidden input for start time with 24-hour format
   */
  private updateStartTimeHiddenInput() {
    if (this.startTimeHiddenInput && this.startTimeInput.value) {
      this.startTimeHiddenInput.value = convertTo24HourFormat(this.startTimeInput.value);
    }
  }

  /**
   * Update the hidden input for end time with 24-hour format
   */
  private updateEndTimeHiddenInput() {
    if (this.endTimeHiddenInput && this.endTimeInput.value) {
      this.endTimeHiddenInput.value = convertTo24HourFormat(this.endTimeInput.value);
    }
  }

  render() {
    return (
      <Host>
        <div class="time-inputs">
          <div class="time-input-container">
            <input
              type="text"
              id="startTimeInput"
              class={`time-input ${this.customInputClass || ''}`}
              placeholder="Start time"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              ref={ref => (this.startTimeInput = ref as HTMLInputElement)}
              onInput={this.handleStartTimeInput}
              onFocus={this.handleStartTimeFocus}
              onBlur={this.handleStartTimeBlur}
              onKeyDown={this.handleStartTimeKeyDown}
              disabled={this.readonly}
            />
            {this.startTimeName && (
              <input
                type="hidden"
                name={this.startTimeName}
                ref={ref => (this.startTimeHiddenInput = ref as HTMLInputElement)}
              />
            )}

            <div
              id="startTimeDropdown"
              class="time-dropdown"
              ref={ref => (this.startTimeDropdown = ref as HTMLDivElement)}
            ></div>
          </div>
          {!this.single && <slot name="joiner">to</slot>}
          {!this.single && (
            <div class="time-input-container">
              <input
                type="text"
                id="endTimeInput"
                class={`time-input ${this.customInputClass || ''}`}
                placeholder="End time"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                ref={ref => (this.endTimeInput = ref as HTMLInputElement)}
                onInput={this.handleEndTimeInput}
                onFocus={this.handleEndTimeFocus}
                onBlur={this.handleEndTimeBlur}
                onKeyDown={this.handleEndTimeKeyDown}
                disabled={this.readonly}
              />
              {this.endTimeName && (
                <input
                  type="hidden"
                  name={this.endTimeName}
                  ref={ref => (this.endTimeHiddenInput = ref as HTMLInputElement)}
                />
              )}
              <div
                id="endTimeDropdown"
                class="time-dropdown"
                ref={ref => (this.endTimeDropdown = ref as HTMLDivElement)}
              ></div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
